import { Injectable, Provider } from "@angular/core";
import { MatomoConfiguration, MatomoTracker, MATOMO_CONFIGURATION, PageUrlProvider, MATOMO_PAGE_URL_PROVIDER } from "ngx-matomo-client";
import { BehaviorSubject, Observable, of } from "rxjs";
import { Router, NavigationEnd } from "@angular/router";

// TODO 14420 - Matomo - implement new Matomo tracking package
//import { MatomoConfiguration } from "@ngx-matomo/tracker";

export interface MatomoConfigType {
    siteId: number;
    trackerUrl: string;
    siteRoute: string;
    mode: "auto" | "manual" | "deferred";
}

const initialConfig: MatomoConfigType = {
    siteId: 2334,
    trackerUrl: "https://matomo.eforsyning.dk/matomo.php",
    siteRoute: "",
    mode: "auto"
};

@Injectable({
    providedIn: "root"
})
export class MatomoPageUrlProvider implements PageUrlProvider {
    private siteRoute: string = "";

    constructor(private router: Router) {}

    setSiteRoute(route: string): void {
        this.siteRoute = route;
    }

    getCurrentPageUrl(event: NavigationEnd): Observable<string> {
        const href = document.getElementsByTagName("base")[0].href;
        const routerUrl = event.urlAfterRedirects;
        // Use the href as the base and append the router URL
        const fullUrl = href + routerUrl.substring(1); // Remove leading slash from routerUrl
        return of(fullUrl);
    }
}

@Injectable({
    providedIn: "root"
})
export class MatomoConfigService {
    private currentConfig = new BehaviorSubject<MatomoConfigType>(initialConfig);

    constructor(
        private matomoTracker: MatomoTracker,
        private pageUrlProvider: MatomoPageUrlProvider
    ) {
        // Initialize tracking with default configuration
        this.initializeTracking(initialConfig);
        this.trackPWAStatus();

        // Track PWA launch
        window.matchMedia("(display-mode: standalone)").addEventListener("change", (evt) => {
            if (evt.matches) {
                this.matomoTracker.trackEvent("Application", "PWA Launch", "App Started");
            }
        });

        // Track initial PWA launch
        if (window.matchMedia("(display-mode: standalone)").matches) {
            this.matomoTracker.trackEvent("Application", "PWA Launch", "App Started");
        }
    }

    private trackPWAStatus(): void {
        // Check if running in PWA mode
        const isPWA = window.matchMedia("(display-mode: standalone)").matches || (window.navigator as any).standalone || document.referrer.includes("android-app://");

        // Track PWA status
        this.matomoTracker.trackEvent("Application", "PWA Status", isPWA ? "Running as PWA" : "Running in Browser");

        // Add PWA status to custom dimension if you have one set up
        // this.matomoTracker.setCustomDimension(1, isPWA ? 'PWA' : 'Browser');
    }

    private initializeTracking(config: MatomoConfigType): void {
        // Keep the original tracker URL
        this.matomoTracker.setTrackerUrl(config.trackerUrl);
        this.matomoTracker.setSiteId(config.siteId.toString());
        this.matomoTracker.enableLinkTracking(true);
        this.matomoTracker.enableHeartBeatTimer(15);

        // Update the page URL provider with the current site route
        this.pageUrlProvider.setSiteRoute(config.siteRoute);
    }

    public updateSiteId(siteId: number): void {
        const newConfig = {
            ...this.currentConfig.value,
            siteId
        };
        this.currentConfig.next(newConfig);
        this.initializeTracking(newConfig);
    }

    public getCurrentConfig(): MatomoConfigType {
        return this.currentConfig.value;
    }

    public buildConfig(matomoSiteId: number, siteRoute: string = ""): MatomoConfigType {
        const config = {
            ...initialConfig,
            siteId: matomoSiteId,
            siteRoute
        };
        this.currentConfig.next(config);
        this.initializeTracking(config);
        return config;
    }
}

export const matomoProviders: Provider[] = [
    {
        provide: MATOMO_CONFIGURATION,
        useValue: initialConfig
    },
    {
        provide: MATOMO_PAGE_URL_PROVIDER,
        useClass: MatomoPageUrlProvider
    }
];
